import moment from 'moment';
import React from 'react';

/**
 * Convertit une chaîne de caractères représentant une date en différents formats.
 * Exemples d'utilisation
 * console.log(formatDate('2024-08-15T13:45:30', {null or 'eu'}));  // "15/08/2024"
 * console.log(formatDate('2024-08-15T13:45:30', 'us'));  // "08/15/2024"
 * console.log(formatDate('2024-08-15T13:45:30', 'iso')); // "2024-08-15"
 * console.log(formatDate('2024-08-15T13:45:30', 'sql')); // "2024-08-15 13:45:30"
 * @param {string} dateString La chaîne de caractères représentant la date.
 * @param {string} format defalut 'eu'  Le format de la date souhaitée ('eu', 'us', 'iso', 'sql').
 * @returns {string} La date formatée.
 */
const formatDate = (dateString, format = 'eu') => {
  const date = new Date(dateString); // Convertit la chaîne de caractères en objet Date

  // Récupère les composants de la date
  const day = date.getDate();
  const month = date.getMonth() + 1;
  const year = date.getFullYear();

  // Ajoute un zéro devant le jour et le mois si nécessaire
  const formattedDay = day < 10 ? `0${day}` : day;
  const formattedMonth = month < 10 ? `0${month}` : month;

  switch (format.toLowerCase()) {
    case 'eu': // Format européen 'jj/mm/yyyy'
      return `${formattedDay}/${formattedMonth}/${year}`;
    case 'us': // Format américain 'mm/dd/yyyy'
      return `${formattedMonth}/${formattedDay}/${year}`;
    case 'iso': // Format ISO 'yyyy-mm-dd'
      return `${year}-${formattedMonth}-${formattedDay}`;
    case 'sql': // Format SQL 'yyyy-mm-dd hh:mm:ss'
      const hours = date.getHours();
      const minutes = date.getMinutes();
      const seconds = date.getSeconds();
      const formattedHours = hours < 10 ? `0${hours}` : hours;
      const formattedMinutes = minutes < 10 ? `0${minutes}` : minutes;
      const formattedSeconds = seconds < 10 ? `0${seconds}` : seconds;
      return `${year}-${formattedMonth}-${formattedDay} ${formattedHours}:${formattedMinutes}:${formattedSeconds}`;
    default: // Retourne le format européen par défaut si aucun format valide n'est fourni
      return `${formattedDay}/${formattedMonth}/${year}`;
  }
};


/**
 * Composant fonctionnel qui affiche une date formatée.
 * @param {Object} props Les propriétés du composant.
 * @param {string} props.dateString La chaîne de caractères représentant la date.
 * @returns {JSX.Element} Le composant JSX.
 */
const DateFormatted = ({ dateString }) => {

  const formattedDate = formatDate(dateString); // Appelle la fonction formatDate pour formater la date
  return <span>{formattedDate}</span>; // Retourne un élément JSX affichant la date formatée

};

/**
 * Convertit un timestamp en une chaîne de caractères au format "jj.mm.yyyy hh:mm".
 * @param {number} timestamp Le timestamp à convertir.
 * @returns {string} La chaîne de caractères formatée.
 */
const formatTimeToDate = (timestamp, type = false, gmt = -2) => {
  if (!timestamp) return '';
  const date = new Date(timestamp * 1000); // Convertit le timestamp en objet Date   
  // décaler de gmt heures pour le décalage GMT+X
  date.setUTCHours(date.getUTCHours() + gmt);
  const day = ("0" + date.getDate()).slice(-2);// Récupère le jour, ajoute un zéro devant si nécessaire
  const month = ("0" + (date.getMonth() + 1)).slice(-2);// Récupère le mois, ajoute un zéro devant si nécessaire
  const year = date.getFullYear(); // Récupère l'année
  const hours = ("0" + date.getHours()).slice(-2);// Récupère les heures, ajoute un zéro devant si nécessaire
  const minutes = ("0" + date.getMinutes()).slice(-2);// Récupère les minutes, ajoute un zéro devant si nécessaire
  switch (type) {
    case 'time':
      return `${hours}:${minutes}`;
    case 'small':
      return `${day}.${month}.${year}`;
    default:
      return `${day}.${month}.${year} ${hours}:${minutes}`;
  }
}

const dateToDateAge = (value) => {
  const dateNaissance = moment(value); // Convertir la valeur en objet moment
  const dateFormatee = dateNaissance.format('DD.MM.YYYY');
  const differenceAnnees = moment().diff(dateNaissance, 'years'); // Calculer la différence en années
  return `${dateFormatee} - ${differenceAnnees} ans`;
}

const stDateTimeFromDate = (date) => {
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');

  return `${day}/${month}/${year} ${hours}:${minutes}`;
};

export default DateFormatted;
export { formatDate, formatTimeToDate, dateToDateAge, stDateTimeFromDate };
